import React from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import AndroidQR from '../../assets/images/google-qr.svg?url'
import AppleQR from '../../assets/images/apple-qr.svg?url'
import Devices from '../../assets/images/devices.svg?url'
import './MobileSupport.scss'
import { Button } from '../../components/Button/Button'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'
import { useSessionAvailability } from '../../components/ScopedBlock/hooks'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { useDocumentTitle } from '../../common/useDocumentTitle'

const DeviceIconWrapper = styled.div`
  display: inline-flex;
  border-radius: 0.25rem;
  background: rgba(119, 73, 248, 0.1);
  padding: 0.625rem;
  justify-content: center;
  align-items: center;
  color: var(--zocks-primary);

  i {
    font-size: 1.25rem;
  }
`

export const MobileSupport: React.FC = () => {
  useDocumentTitle('In Person Meeting')
  const { available: sessionAvailable, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()
  const navigate = useNavigate()
  const atRoomSessionLimitActions = () => {
    return sessionAvailabilitySuccess && sessionAvailable
  }

  const handleClickInPersonMeeting = () => {
    navigate(`/workspace/in-person-meeting/live-meetings`)
  }

  return (
    <Container>
      <Row className='zocks-mb-2.5'>
        <h3>In Person Meeting</h3>
      </Row>
      <Container className='mobile-support-container'>
        <Row className='ms-xl-2 zocks-mb-3.5'>
          <Col xl={7} md={12}>
            <Row>
              <Col>
                <h2 className='zocks-mb-1'> Be present at your in-person meetings </h2>
                <div className='paragraph-medium zocks-mb-2.5'>
                  Our app analyzes advisor-client conversations with precision, ensuring no detail is missed. Post-meeting, receive an AI-generated summary
                  highlighting crucial points and follow-up actions, enhancing efficiency and service quality.
                </div>
              </Col>
            </Row>
            <CheckListElement listElement='Accurate meeting capture' />
            <CheckListElement listElement='AI-summarized insights' />
            <CheckListElement listElement='Streamlined follow-ups' />
          </Col>
          <Col className='text-center p-4' xl={5} md={12}>
            <img className='devices-image' alt='phone' src={Devices} />
          </Col>
        </Row>
        <Container className='zocks-mt-2.5 p-0 container-fluid'>
          <Row className='gx-2 gap-3'>
            <Col className='download-app-container download-app-md' xl md={12}>
              <Container>
                <Row>
                  <h3 className='zocks-mb-2 p-0'>Download the Zocks app</h3>
                </Row>
                <Row className='gap-3'>
                  <Col className='download-container' xs={12} md>
                    <DownloadApp
                      qrImage={AndroidQR}
                      text='Get in on'
                      icon='bi bi-android2'
                      platformName='Google Play'
                      link='https://play.google.com/store/apps/details?id=io.zocks.flutter_mobile_app'
                    />
                  </Col>
                  <Col className='download-container' xs={12} md>
                    <DownloadApp
                      qrImage={AppleQR}
                      text='Download on the'
                      icon='bi bi-apple'
                      platformName='App Store'
                      link='https://apps.apple.com/us/app/zocks/id6443466353'
                    />
                  </Col>
                </Row>
              </Container>
            </Col>
            <Col className='web-app-container d-none d-sm-flex' xl={4} md={12}>
              <div className='devide-row d-flex flex-row gap-2'>
                <div>
                  <DeviceIconWrapper>
                    <i className='bi bi-laptop'> </i>
                  </DeviceIconWrapper>
                </div>
                <div>
                  <DeviceIconWrapper>
                    <i className='bi bi-phone'> </i>
                  </DeviceIconWrapper>
                </div>
              </div>
              <h3 className='text-center zocks-mb-1'>Use our web application</h3>

              <ScopedBlock
                scope={{
                  scope: 'room_session:start',
                  superScope: ['room_session:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <ScopedBlock authActions={[atRoomSessionLimitActions]} missingScopeAction={MissingScopeAction.DISABLE}>
                  <Button variant='primary' onClick={handleClickInPersonMeeting}>
                    <div className='button-large text-center'>Create In Person Meeting</div>
                  </Button>
                </ScopedBlock>
              </ScopedBlock>
            </Col>
          </Row>
        </Container>
      </Container>
    </Container>
  )
}

type CheckListElementProps = {
  listElement: string
}

const CheckListElement: React.FC<CheckListElementProps> = ({ listElement }) => {
  return (
    <div className='d-flex align-items-center gap-2 mb-2'>
      <i className='bi bi-check-circle-fill'></i>
      <div className='paragraph-medium'>{listElement}</div>
    </div>
  )
}

type QRElementProps = {
  qrImage: string
  text: string
  icon: string
  platformName: string
  link: string
}
const DownloadApp: React.FC<QRElementProps> = ({ qrImage, text, icon, platformName, link }) => {
  return (
    <div className='d-md-flex justify-content-md-around align-items-center'>
      <div className='display-qr'>
        <div className='qr-container'>
          <img className='qr-img' alt='QR code' src={qrImage} />
        </div>
      </div>
      <div>
        <div className='zocks-mb-0.75 paragraph-extra-small'>{text}</div>
        <Button className='button-larg full-width-button custom-button' variant='white' onClick={() => (window.location.href = link)}>
          <i className={icon}></i>
          {platformName}
        </Button>
      </div>
    </div>
  )
}
